import { useEffect, useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  Typography,
  Button,
  Row,
  Col,
  Statistic,
  Tag,
  Alert,
  Spin,
  message,
} from "antd";
import { ThemeContext } from "../../context/ThemeContext";
import { isAdmin } from "../../utils/roleUtils";
import {
  BankOutlined,
  TrophyOutlined,
  BarChartOutlined,
  FileTextOutlined,
  GlobalOutlined,
  ArrowLeftOutlined,
  ReloadOutlined,
  UserOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import apiClient from "../../services/apiClient";
import "./IssuerDetails.css";
import "../Admin/AdminPages.css";

const { Title, Text, Paragraph } = Typography;

function IssuerDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const cin = location.state?.cin;
  const { isDark } = useContext(ThemeContext);

  const [issuerData, setIssuerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalIsins: 0,
    totalRatings: 0,
    totalFinancials: 0,
    totalNews: 0,
  });
  const baseUrl =
    process.env.REACT_APP_BASE_URL || "http://localhost:8080/api/v1/sb";

  // Fetch issuer data
  const fetchIssuerData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(
        `${baseUrl}/master-issuer/${encodeURIComponent(cin)}`
      );

      if (response.data.statusCode === 200) {
        setIssuerData(response.data.data);
        // Fetch stats for each section
        await fetchStats(response.data.data.issuer);
      } else {
        throw new Error(response.data.message || "Failed to fetch issuer data");
      }
    } catch (error) {
      setError("Error fetching issuer data");
      message.error("Error fetching issuer data");
      console.error("Error fetching issuer data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics for different sections
  const fetchStats = async (issuerName) => {
    try {
      // Call the new single API
      const response = await apiClient.get(
        `${baseUrl}/master-issuer/get-count/${encodeURIComponent(issuerName)}`
      );
      if (response.data && response.data.statusCode === 200 && response.data.data) {
        setStats({
          totalIsins: response.data.data.isinCount || 0,
          totalRatings: response.data.data.ratingsCount || 0,
          totalFinancials: response.data.data.financialsCount || 0,
          totalNews: 0,
        });
      } else {
        setStats({ totalIsins: 0, totalRatings: 0, totalFinancials: 0, totalNews: 0 });
      }
    } catch (error) {
      setStats({ totalIsins: 0, totalRatings: 0, totalFinancials: 0, totalNews: 0 });
      // Continue with default stats if API call fails
    }
  };

  useEffect(() => {
    if (cin) {
      fetchIssuerData();
    }
  }, [cin]);

  const handleRefresh = () => {
    fetchIssuerData();
  };

  const handleGoBack = () => {
    navigate("/d/issuer-list");
  };

  const handleNavigateToSection = (section) => {
    const basePath = isAdmin() ? "/admin" : "/d";
    
    if (section === "isins") {
      navigate(`${basePath}/issuer/isins`, {
        state: { cin: issuerData.corporateIdentityNumber, issuerName: issuerData.issuer },
      });
    } else if (section === "financials") {
      navigate(`${basePath}/issuer/financials`, {
        state: { issuerName: issuerData.issuer },
      });
    } else if (section === "ratings") {
      navigate(`${basePath}/issuer/ratings`, {
        state: { issuerName: issuerData.issuer },
      });
    } else {
      console.log("issuerData.cin", issuerData);
      navigate(`${basePath}/issuer/${encodeURIComponent(issuerData.corporateIdentityNumber)}/${section}`);
    }
  };

  const detailSections = [
    {
      key: "isins",
      title: "ISINs",
      subtitle: "Bond Instruments",
      description: "Explore all bond instruments issued by this entity",
      icon: <FileTextOutlined />,
      color: "#667eea",
      count: stats.totalIsins,
      route: "isins",
    },
    {
      key: "ratings",
      title: "Credit Ratings",
      subtitle: "Rating History",
      description: "Track credit rating changes and assessments",
      icon: <TrophyOutlined />,
      color: "#4ecdc4",
      count: stats.totalRatings,
      route: "ratings",
    },
    {
      key: "financials",
      title: "Financial Data",
      subtitle: "Performance Metrics",
      description: "Analyze financial performance and key metrics",
      icon: <BarChartOutlined />,
      color: "#ff6b6b",
      count: stats.totalFinancials,
      route: "financials",
    },
    {
      key: "news",
      title: "News & Updates",
      subtitle: "Latest Information",
      description: "Stay updated with latest news and announcements",
      icon: <GlobalOutlined />,
      color: "#feca57",
      count: stats.totalNews,
      route: "news",
    },
  ];

  if (!cin) {
    return (
      <div className={`admin-page ${isDark ? "dark" : ""}`}>
        <Alert
          message="Error"
          description="No issuer selected. Please navigate to this page from the issuer list."
          type="error"
          showIcon
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`admin-page ${isDark ? "dark" : ""}`}>
        <div className="loading-container">
          <Spin size="large" />
          <Text>Loading issuer details...</Text>
        </div>
      </div>
    );
  }

  if (error || !issuerData) {
    return (
      <div className={`admin-page ${isDark ? "dark" : ""}`}>
        <Alert
          message="Error"
          description={error || "Issuer not found"}
          type="error"
          showIcon
          action={
            <Button size="small" danger onClick={handleGoBack}>
              Go Back
            </Button>
          }
        />
      </div>
    );
  }

  // Stats for hero section
  const heroStats = [
    {
      number: stats.totalIsins.toLocaleString(),
      label: "Total ISINs",
      icon: <FileTextOutlined />,
    },
    {
      number: stats.totalRatings.toLocaleString(),
      label: "Credit Ratings",
      icon: <BarChartOutlined />,
    },
    {
      number: stats.totalFinancials.toLocaleString(),
      label: "Financial Records",
      icon: <TrophyOutlined />,
    },
    {
      number: stats.totalNews.toLocaleString(),
      label: "News Articles",
      icon: <GlobalOutlined />,
    },
  ];

  return (
    <div className={`admin-page ${isDark ? "dark" : ""}`}>
      {/* Hero Section */}
      <div className="admin-header-banner">
        <div className="hero-content">
          <Title level={1} className="hero-title-news">
            {issuerData.issuer}
          </Title>
          <div style={{ marginTop: 12, display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Tag
              color="blue"
        style={{
                fontSize: 14,
                padding: '6px 12px',
                borderRadius: '6px',
                fontWeight: 500
              }}
            >
              {issuerData.sector || "N/A"}
            </Tag>
            <Tag
              color="green"
          style={{
                fontSize: 14,
                padding: '6px 12px',
                borderRadius: '6px',
                fontWeight: 500
              }}
            >
              {issuerData.typeOfIssuerBasedOnOwnership || "N/A"}
            </Tag>
            <Tag
              color="orange"
            style={{
                fontSize: 14,
                padding: '6px 12px',
                borderRadius: '6px',
                        fontWeight: 500
              }}
            >
              {issuerData.industry || "N/A"}
            </Tag>
          </div>
        </div>
      </div>


      {/* Summary Information Section */}
      <div style={{ marginBottom: 32 }}>
     
        <Row gutter={[16, 16]}>
          {/* First Row - Summary Cards */}
          <Col xs={24} sm={12} md={6}>
            <div
              style={{
                background: isDark ? "#2a2a3e" : "#fff",
                border: isDark ? "1px solid #3a3a4e" : "1px solid #e8e8e8",
                borderRadius: 8,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                padding: "16px",
                position: "relative",
                height: '100px',
                overflow: "hidden"
              }}
            >
              <div style={{ 
                position: "absolute",
                top: "16px",
                left: "16px",
                fontSize: 12,
                fontWeight: 500,
                color: isDark ? "#ffffff" : "#666",
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                Corporate Identity Number
        <Button
          type="text"
                  size="small" 
                  icon={<CopyOutlined />}
                  onClick={() => {
                    navigator.clipboard.writeText(issuerData.corporateIdentityNumber || '');
                    message.success('CIN copied to clipboard!');
                  }}
                />
      </div>
              <div style={{ 
                position: "absolute",
                bottom: "16px",
                left: "16px",
                fontSize: 14, 
                fontWeight: 600, 
                color: isDark ? "#ffffff" : "#000",
                fontFamily: 'monospace',
                lineHeight: 1,
                zIndex: 1
              }}>
                {issuerData.corporateIdentityNumber || "N/A"}
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div
              style={{
                background: isDark ? "#2a2a3e" : "#fff",
                border: isDark ? "1px solid #3a3a4e" : "1px solid #e8e8e8",
                borderRadius: 8,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                padding: "16px",
                position: "relative",
                height: '100px',
                overflow: "hidden"
              }}
            >
              <div style={{ 
                position: "absolute",
                top: "16px",
                left: "16px",
                fontSize: 12,
                fontWeight: 500,
                color: isDark ? "#ffffff" : "#666",
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                Legal Entity Identifier
                <Button 
                  type="text" 
                  size="small" 
                  icon={<CopyOutlined />}
                  onClick={() => {
                    navigator.clipboard.writeText(issuerData.legalEntityIdentifier || '');
                    message.success('LEI copied to clipboard!');
                  }}
                />
              </div>
              <div style={{ 
                position: "absolute",
                bottom: "16px",
                left: "16px",
                fontSize: 14, 
                fontWeight: 600, 
                color: isDark ? "#ffffff" : "#000",
                fontFamily: 'monospace',
                lineHeight: 1,
                zIndex: 1
              }}>
                {issuerData.legalEntityIdentifier || "N/A"}
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div
              style={{
                background: isDark ? "#2a2a3e" : "#fff",
                border: isDark ? "1px solid #3a3a4e" : "1px solid #e8e8e8",
                borderRadius: 8,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                padding: "16px",
                position: "relative",
                height: '100px',
                overflow: "hidden"
              }}
            >
              <div style={{ 
                position: "absolute",
                top: "16px",
                left: "16px",
                fontSize: 12,
                fontWeight: 500,
                color: isDark ? "#ffffff" : "#666",
                zIndex: 2
              }}>
                Basic Industry
              </div>
              <div style={{ 
                position: "absolute",
                bottom: "16px",
                left: "16px",
                fontSize: 16, 
                fontWeight: 600, 
                color: isDark ? "#ffffff" : "#000",
                lineHeight: 1,
                zIndex: 1
              }}>
                {issuerData.basicIndustry || "N/A"}
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div
              style={{
                background: isDark ? "#2a2a3e" : "#fff",
                border: isDark ? "1px solid #3a3a4e" : "1px solid #e8e8e8",
                borderRadius: 8,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                padding: "16px",
                position: "relative",
                height: '100px',
                overflow: "hidden"
              }}
            >
              <div style={{ 
                position: "absolute",
                top: "16px",
                left: "16px",
                fontSize: 12,
                fontWeight: 500,
                color: isDark ? "#ffffff" : "#666",
                zIndex: 2
              }}>
                Macroeconomic Sector
              </div>
              <div style={{ 
                position: "absolute",
                bottom: "16px",
                left: "16px",
                fontSize: 16, 
                fontWeight: 600, 
                color: isDark ? "#ffffff" : "#000",
                lineHeight: 1,
                zIndex: 1
              }}>
                {issuerData.macroeconomicSector || "N/A"}
              </div>
            </div>
          </Col>
        </Row>

        {/* Second Row - Statistics Cards */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <div
              style={{
                background: isDark ? "#2a2a3e" : "#fff",
                border: isDark ? "1px solid #3a3a4e" : "1px solid #e8e8e8",
                borderRadius: 12,
                boxShadow: isDark ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.06)",
                padding: "20px",
                position: "relative",
                height: 200,
                overflow: "hidden"
              }}
            >
              <div style={{ 
                position: "absolute",
                top: "16px",
                left: "16px",
                fontSize: 19,
                fontWeight: 600,
                fontStyle: "Semi Bold",
                color: isDark ? "#ffffff" : "#000",
                zIndex: 2
              }}>
                Total ISINs
              </div>
              <div style={{ 
                position: "absolute",
                bottom: "20px",
                left: "16px",
                fontSize: 36, 
                fontWeight: 700, 
                fontStyle: "bold",
                color: isDark ? "#ffffff" : "#000",
                lineHeight: 1,
                zIndex: 1
              }}>
                {stats.totalIsins}
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div
              style={{
                background: isDark ? "#2a2a3e" : "#fff",
                border: isDark ? "1px solid #3a3a4e" : "1px solid #e8e8e8",
                borderRadius: 12,
                boxShadow: isDark ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.06)",
                padding: "20px",
                position: "relative",
                height: 200,
                overflow: "hidden"
              }}
            >
              <div style={{ 
                position: "absolute",
                top: "16px",
                left: "16px",
                fontSize: 19,
                fontWeight: 600,
                fontStyle: "Semi Bold",
                color: isDark ? "#ffffff" : "#000",
                zIndex: 2
              }}>
                Credit Rating Agencies
              </div>
              <div style={{ 
                position: "absolute",
                bottom: "20px",
                left: "16px",
                fontSize: 36, 
                fontWeight: 700, 
                fontStyle: "bold",
                color: isDark ? "#ffffff" : "#000",
                lineHeight: 1,
                zIndex: 1
              }}>
                {stats.totalRatings}
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div
              style={{
                background: isDark ? "#2a2a3e" : "#fff",
                border: isDark ? "1px solid #3a3a4e" : "1px solid #e8e8e8",
                borderRadius: 12,
                boxShadow: isDark ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.06)",
                padding: "20px",
                position: "relative",
                height: 200,
                overflow: "hidden"
              }}
            >
              <div style={{ 
                position: "absolute",
                top: "16px",
                left: "16px",
                fontSize: 19,
                fontWeight: 600,
                fontStyle: "Semi Bold",
                color: isDark ? "#ffffff" : "#000",
                zIndex: 2
              }}>
                Financial Records
              </div>
              <div style={{ 
                position: "absolute",
                bottom: "20px",
                left: "16px",
                fontSize: 36, 
                fontWeight: 700, 
                fontStyle: "bold",
                color: isDark ? "#ffffff" : "#000",
                lineHeight: 1,
                zIndex: 1
              }}>
                {stats.totalFinancials}
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div
              style={{
                background: isDark ? "#2a2a3e" : "#fff",
                border: isDark ? "1px solid #3a3a4e" : "1px solid #e8e8e8",
                borderRadius: 12,
                boxShadow: isDark ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.06)",
                padding: "20px",
                position: "relative",
                height: 200,
                overflow: "hidden"
              }}
            >
              <div style={{ 
                position: "absolute",
                top: "16px",
                left: "16px",
                fontSize: 19,
                fontWeight: 600,
                fontStyle: "Semi Bold",
                color: isDark ? "#ffffff" : "#000",
                zIndex: 2
              }}>
                News Articles
              </div>
              <div style={{ 
                position: "absolute",
                bottom: "20px",
                left: "16px",
                fontSize: 36, 
                fontWeight: 700, 
                fontStyle: "bold",
                color: isDark ? "#ffffff" : "#000",
                lineHeight: 1,
                zIndex: 1
              }}>
                {stats.totalNews}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Available Information Section */}
      <div style={{ marginBottom: 32 }}>
        <Title level={3} style={{ marginBottom: 8, fontSize: 30, fontWeight: 800 }}>
          Available Information
        </Title>
        <Paragraph style={{ marginBottom: 24, fontSize: 16, color: '#666' }}>
          Select a category below to explore detailed information about this issuer.
        </Paragraph>

        {/* First Row - 2 Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} lg={12}>
            <Card
              hoverable
              onClick={() => handleNavigateToSection("isins")}
              className="available-info-card-orange"
              style={{
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                border: 'none',
                height: '180px',
                position: 'relative'
              }}
              bodyStyle={{ padding: '20px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
            >
              <div style={{ position: 'absolute', top: '16px', right: '16px' }}>
                <FileTextOutlined style={{ fontSize: 24, color: '#000' }} />
              </div>
              <div>
                <Title level={4} style={{ color: '#000', margin: 0, fontSize: 20, fontWeight: 700 }}>
                  ISINs
                </Title>
                <Text style={{ color: '#000', fontSize: 16, fontWeight: 500, marginBottom: 8, display: 'block' }}>
                  Bond Instruments
                </Text>
                <Paragraph style={{ color: '#000', fontSize: 14, margin: 0, lineHeight: '1.4' }}>
                  Explore all bond instruments issued by this entity
                </Paragraph>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={12}>
              <Card
                hoverable
              onClick={() => handleNavigateToSection("ratings")}
              className="available-info-card-purple"
              style={{
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                border: 'none',
                height: '180px',
                position: 'relative'
              }}
              bodyStyle={{ padding: '20px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
            >
              <div style={{ position: 'absolute', top: '16px', right: '16px' }}>
                <TrophyOutlined style={{ fontSize: 24, color: '#000' }} />
              </div>
              <div>
                <Title level={4} style={{ color: '#000', margin: 0, fontSize: 20, fontWeight: 700 }}>
                  Credit Ratings
                </Title>
                <Text style={{ color: '#000', fontSize: 16, fontWeight: 500, marginBottom: 8, display: 'block' }}>
                  Rating History
                </Text>
                <Paragraph style={{ color: '#000', fontSize: 14, margin: 0, lineHeight: '1.4' }}>
                  Track credit rating changes and assessments
                </Paragraph>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Second Row - 2 Cards */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={12}>
            <Card
              hoverable
              onClick={() => handleNavigateToSection("financials")}
              className="available-info-card-green"
                    style={{
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                border: 'none',
                height: '180px',
                position: 'relative'
              }}
              bodyStyle={{ padding: '20px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
            >
              <div style={{ position: 'absolute', top: '16px', right: '16px' }}>
                <BarChartOutlined style={{ fontSize: 24, color: '#000' }} />
                  </div>
              <div>
                <Title level={4} style={{ color: '#000', margin: 0, fontSize: 20, fontWeight: 700 }}>
                  Financial Data
                    </Title>
                <Text style={{ color: '#000', fontSize: 16, fontWeight: 500, marginBottom: 8, display: 'block' }}>
                  Performance Metrics
                    </Text>
                <Paragraph style={{ color: '#000', fontSize: 14, margin: 0, lineHeight: '1.4' }}>
                  Explore financial performance and key metrics
                    </Paragraph>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={12}>
            <Card
              hoverable
              onClick={() => handleNavigateToSection("news")}
              className="available-info-card-pink"
              style={{
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                border: 'none',
                height: '180px',
                position: 'relative'
              }}
              bodyStyle={{ padding: '20px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
            >
              <div style={{ position: 'absolute', top: '16px', right: '16px' }}>
                <GlobalOutlined style={{ fontSize: 24, color: '#000' }} />
                  </div>
              <div>
                <Title level={4} style={{ color: '#000', margin: 0, fontSize: 20, fontWeight: 700 }}>
                  News & Updates
                </Title>
                <Text style={{ color: '#000', fontSize: 16, fontWeight: 500, marginBottom: 8, display: 'block' }}>
                  Latest Information
                </Text>
                <Paragraph style={{ color: '#000', fontSize: 14, margin: 0, lineHeight: '1.4' }}>
                  Stay updated with latest news and developments
                </Paragraph>
                </div>
              </Card>
            </Col>
        </Row>
      </div>
    </div>
  );
}

export default IssuerDetails;
